import React from 'react';
import {
  Circle,
  Hexagon,
  XCircle,
  Disc,
  Table2,
  Grid,
  Triangle,
  Shield,
  Waves,
  MapPin,
  Box,
  Type,
  Minus,
  User,
  Hand,
  Armchair,
  Brush,
  Wind,
  RefreshCw,
  Star,
  ArrowUpDown,
  Layers,
  DoorOpen,
  RectangleHorizontal,
  BedDouble,
  Sofa,
  Car as CarIcon,
} from 'lucide-react';
import { useEditorStore } from '@/core/stores/editor-store';
import { PanelSection, cn } from '@/ui/shared';

interface LibraryItem {
  type: string;
  variant: string;
  icon: React.ReactNode;
  label: string;
  color?: string;
}

interface LibrarySection {
  title: string;
  items: LibraryItem[];
}

// Colors match what the 2D canvas and 3D scene actually draw, so the library
// icon previews what the user is about to place.
const TAN = '#c4a265';       // IDPA / paper silhouette
const STEEL = '#9ca3af';     // steel targets, popper heads
const BARREL = '#8b4513';    // saddle brown (matches renderer.ts)
const TABLE = '#1f2937';     // near-black (matches renderer.ts)
const HARD_COVER = '#4b5563';
const SOFT_COVER = '#a8a29e';
const FAULT_LINE = '#ef4444';
const CONE = '#fb923c';
const NO_SHOOT = '#3b82f6';  // blue marks (the silhouette stays tan)
const SHIRT = '#3b5f8a';     // shooter torso color (matches 3D SHIRT)
const FURNITURE_WOOD = '#a87339';   // matches theme FURNITURE.wood
const FURNITURE_FABRIC = '#5b7c99'; // matches theme FURNITURE.fabric
const CAR_BODY = '#b91c1c';         // matches theme CAR.body

const library: LibrarySection[] = [
  {
    title: 'Targets',
    items: [
      {
        type: 'paper_target',
        variant: 'idpa',
        icon: <User size={16} />,
        label: 'IDPA Target',
        color: TAN,
      },
      {
        type: 'paper_target',
        variant: 'ipsc_classic',
        icon: <User size={16} />,
        label: 'Paper Target (Classic)',
        color: TAN,
      },
      {
        type: 'paper_target',
        variant: 'ipsc_mini',
        icon: <User size={14} />,
        label: 'Paper Target (Mini)',
        color: TAN,
      },
      {
        type: 'steel_target',
        variant: 'round_8',
        icon: <Circle size={14} />,
        label: 'Steel Plate (8")',
        color: STEEL,
      },
      {
        type: 'steel_target',
        variant: 'round_10',
        icon: <Circle size={15} />,
        label: 'Steel Plate (10")',
        color: STEEL,
      },
      {
        type: 'steel_target',
        variant: 'round_12',
        icon: <Circle size={16} />,
        label: 'Steel Plate (12")',
        color: STEEL,
      },
      {
        type: 'popper',
        variant: 'standard',
        icon: <Hexagon size={16} />,
        label: 'Popper (Standard)',
        color: STEEL,
      },
      {
        type: 'popper',
        variant: 'mini',
        icon: <Hexagon size={14} />,
        label: 'Popper (Mini)',
        color: STEEL,
      },
      {
        type: 'no_shoot',
        variant: 'standard',
        icon: <XCircle size={16} />,
        label: 'No-Shoot',
        color: NO_SHOOT,
      },
    ],
  },
  {
    title: 'Moving Targets',
    items: [
      {
        type: 'plate',
        variant: 'standard',
        icon: <Circle size={16} />,
        label: 'Steel Plate (on stand)',
        color: STEEL,
      },
      {
        type: 'swinger',
        variant: 'round_8',
        icon: <Wind size={16} />,
        label: 'Swinger (steel)',
        color: STEEL,
      },
      {
        type: 'paper_swinger',
        variant: 'idpa',
        icon: <Wind size={16} />,
        label: 'IDPA Swinger (paper)',
        color: TAN,
      },
      {
        type: 'paper_swinger_once',
        variant: 'idpa',
        icon: <Wind size={14} />,
        label: 'IDPA One-Time Swinger',
        color: TAN,
      },
      {
        type: 'drop_turner',
        variant: 'round_8',
        icon: <RefreshCw size={16} />,
        label: 'Drop Turner',
        color: STEEL,
      },
      {
        type: 'texas_star',
        variant: 'standard',
        icon: <Star size={16} />,
        label: 'Texas Star',
        color: STEEL,
      },
      {
        type: 'bobber',
        variant: 'mini',
        icon: <ArrowUpDown size={16} />,
        label: 'Bobber (steel)',
        color: STEEL,
      },
      {
        type: 'paper_popup',
        variant: 'idpa',
        icon: <ArrowUpDown size={16} />,
        label: 'IDPA Paper Popup',
        color: TAN,
      },
    ],
  },
  {
    title: 'Shooter',
    items: [
      {
        type: 'shooter',
        variant: 'shooter_relaxed',
        icon: <User size={16} />,
        label: 'Standing (relaxed)',
        color: SHIRT,
      },
      {
        type: 'shooter',
        variant: 'shooter_hands_up',
        icon: <Hand size={16} />,
        label: 'Standing (hands up)',
        color: SHIRT,
      },
      {
        type: 'shooter',
        variant: 'shooter_seated',
        icon: <Armchair size={16} />,
        label: 'Seated in chair',
        color: SHIRT,
      },
      {
        type: 'shooter',
        variant: 'shooter_broom',
        icon: <Brush size={16} />,
        label: 'Holding broom',
        color: SHIRT,
      },
    ],
  },
  {
    title: 'Props',
    items: [
      {
        type: 'barrel',
        variant: 'standard',
        icon: <Disc size={16} />,
        label: 'Barrel',
        color: BARREL,
      },
      {
        type: 'barrel',
        variant: 'half',
        icon: <Disc size={14} />,
        label: 'Barrel (half)',
        color: BARREL,
      },
      {
        type: 'barrel',
        variant: 'stack_2',
        icon: <Layers size={16} />,
        label: 'Barrel (stack ×2)',
        color: BARREL,
      },
      {
        type: 'barrel',
        variant: 'stack_3',
        icon: <Layers size={18} />,
        label: 'Barrel (stack ×3)',
        color: BARREL,
      },
      {
        type: 'table',
        variant: 'rectangular',
        icon: <Table2 size={16} />,
        label: 'Table',
        color: TABLE,
      },
      {
        type: 'table',
        variant: 'low',
        icon: <Table2 size={14} />,
        label: 'Table (low)',
        color: TABLE,
      },
      {
        type: 'fault_line',
        variant: 'standard',
        icon: <Minus size={16} />,
        label: 'Fault Line',
        color: FAULT_LINE,
      },
      {
        type: 'cone',
        variant: 'standard',
        icon: <Triangle size={16} />,
        label: 'Cone',
        color: CONE,
      },
      {
        type: 'hard_cover',
        variant: 'standard',
        icon: <Shield size={16} />,
        label: 'Hard Cover',
        color: HARD_COVER,
      },
      {
        type: 'soft_cover',
        variant: 'standard',
        icon: <Waves size={16} />,
        label: 'Soft Cover',
        color: SOFT_COVER,
      },
      {
        type: 'soft_cover',
        variant: 'with_window',
        icon: <Waves size={16} />,
        label: 'Soft Cover (with window)',
        color: '#dc2626',
      },
      {
        type: 'soft_cover',
        variant: 'prone',
        icon: <Waves size={16} />,
        label: 'Soft Cover (prone, elevated)',
        color: SOFT_COVER,
      },
    ],
  },
  {
    title: 'Furniture',
    items: [
      {
        type: 'chair',
        variant: 'standard',
        icon: <Armchair size={16} />,
        label: 'Chair',
        color: FURNITURE_WOOD,
      },
      {
        type: 'door',
        variant: 'standard',
        icon: <DoorOpen size={16} />,
        label: 'Doorway (open door)',
        color: FURNITURE_WOOD,
      },
      {
        type: 'window',
        variant: 'standard',
        icon: <RectangleHorizontal size={16} />,
        label: 'Window port',
        color: FURNITURE_WOOD,
      },
      {
        type: 'bed',
        variant: 'standard',
        icon: <BedDouble size={16} />,
        label: 'Bed',
        color: FURNITURE_FABRIC,
      },
      {
        type: 'couch',
        variant: 'standard',
        icon: <Sofa size={16} />,
        label: 'Couch',
        color: FURNITURE_FABRIC,
      },
      {
        type: 'car',
        variant: 'standard',
        icon: <CarIcon size={16} />,
        label: 'Car',
        color: CAR_BODY,
      },
    ],
  },
  {
    title: 'Positions',
    items: [
      {
        type: 'start_position',
        variant: 'standard',
        icon: <MapPin size={16} />,
        label: 'Start Position',
        color: '#10b981',
      },
      {
        type: 'shooting_box',
        variant: 'standard',
        icon: <Box size={16} />,
        label: 'Shooting Box',
        color: '#06b6d4',
      },
      {
        type: 'free_fire_zone',
        variant: 'standard',
        icon: <Grid size={16} />,
        label: 'Free-Fire Zone',
        color: '#8b5cf6',
      },
    ],
  },
  {
    title: 'Annotations',
    items: [
      {
        type: 'text_label',
        variant: 'standard',
        icon: <Type size={16} />,
        label: 'Text Label',
        color: '#6366f1',
      },
      {
        type: 'measurement_line',
        variant: 'standard',
        icon: <Minus size={16} />,
        label: 'Measurement Line',
        color: '#6366f1',
      },
    ],
  },
];

const ObjectLibrary: React.FC = () => {
  const { placingObjectType, setPlacing } = useEditorStore();

  const handleSelect = (type: string, variant: string) => {
    setPlacing(type, variant);
  };

  return (
    <div className="h-full bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
      <div className="p-3 space-y-2">
        {library.map((section) => (
          <PanelSection key={section.title} title={section.title} defaultOpen={true}>
            <div className="space-y-1">
              {section.items.map((item) => (
                <button
                  key={`${item.type}-${item.variant}`}
                  onClick={() => handleSelect(item.type, item.variant)}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData(
                      'application/x-stage-object',
                      JSON.stringify({ type: item.type, variant: item.variant }),
                    );
                    e.dataTransfer.effectAllowed = 'copy';
                  }}
                  className={cn(
                    'w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors cursor-grab active:cursor-grabbing',
                    placingObjectType === item.type
                      ? 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                  )}
                >
                  <div
                    className="flex-shrink-0 text-sm"
                    style={{ color: item.color }}
                  >
                    {item.icon}
                  </div>
                  <span className="flex-1 text-left truncate">
                    {item.label}
                  </span>
                </button>
              ))}
            </div>
          </PanelSection>
        ))}
      </div>
    </div>
  );
};

export default ObjectLibrary;
